import { JsonValue, toMilliseconds } from '@art-suite/art-core-ts';
import { CacheEntry, CacheEntryMetadata, isCacheEntryExpired, TypedCacheEntry } from './CacheEntry';
import { InMemoryPersistenceLayer } from './InMemoryPersistenceLayer';
import { PersistenceLayerInterface } from './PersistenceLayer';
import { PersistenceLayerBase } from './PersistenceLayerBase';

export const NO_SUBSCRIBERS_MEMORY_CACHE_PURGE_DELAY_MS = 100;

export interface CacheStats {
  count: number;
  size: number;
}
/**
 * Cache interface exposed through the context
 */
export interface PersistentCacheInterface<ValueType = unknown> extends PersistenceLayerInterface<ValueType> {
  defaultMaxAgeMs: number;

  // if the data is in-memory and instantly available, we can use these sync methods instead of the async methods
  hasSync(key: string): boolean; // if `hasSync` is true, `has` is true, but not necessarily vice versa
  getSync(key: string): TypedCacheEntry<ValueType> | undefined;
  getMetadataSync(key: string): CacheEntryMetadata | undefined;

  /**
   * Like normal `set`, but does not add the entry to the memory cache (unless it's already in the memory cache, in which case it will be updated).
   * @param key - The key to store the value under.
   * @param value - The value to store.
   * @param expiresAt - The timestamp when the entry will expire.
   * @param createdAt - The timestamp when the entry was created.
   * @param now - The current timestamp.
   * @returns The cache entry.
   */
  setPrefetched(key: string, value: ValueType, expiresAt?: number, createdAt?: number, now?: number): Promise<TypedCacheEntry<ValueType>>;

  /**
   * Subscribes to changes for a specific key. Only notified when the entry's value is updated. (e.g. not notified on 'freshen' or 'purge')
   *
   * Rule: Subscribers are notified when the data is set or freshened, but they are not notified if the expiration is passed.
   * @param key - The key to subscribe to.
   * @param callback - The callback to call when the entry changes.
   * @returns A function to unsubscribe from the key.
   */
  subscribe(key: string, callback: (entry: TypedCacheEntry<ValueType>) => void): () => void;

  /**
   * Subscribes to changes for a specific key. Works the same as 'subscribe', but value is always undefined.
   *
   * Rule: Metadata subscribers are notified when the data is set or freshened, and they are notified if the system notices that the expiration has passed.
   * @param key - The key to subscribe to.
   * @param callback - The callback to call when the entry changes.
   * @returns A function to unsubscribe from the key.
   */
  subscribeToMetadata(key: string, callback: (entry: CacheEntryMetadata) => void): () => void;

  /**
   * Subscribes to all value changes. Callback is invoked from notifySubscribers for every change (set, setPrefetched, getEntry, freshen, freshenAll, purge).
   * @param callback - Called with (key, entry) for each change. For purge events, entry.value will be undefined.
   * @returns Unsubscribe function.
   */
  subscribeToAll(callback: (key: string, entry: TypedCacheEntry<ValueType | undefined>) => void): () => void;

  /**
   * Subscribes to all metadata changes. Callback is invoked from notifyMetadataSubscribers for every change.
   * @param callback - Called with (key, metadata) for each change.
   * @returns Unsubscribe function.
   */
  subscribeToAllMetadata(callback: (key: string, metadata: CacheEntryMetadata) => void): () => void;

  /**
   * Returns the active subscribers and metadata subscribers.
   * @returns map from cache-key to the set of functions to update the subscribers.
   */
  getActiveSubscribers(): Map<string, Set<(entry: TypedCacheEntry<ValueType>) => void>>;

  /**
   * Returns the active metadata subscribers.
   * @returns map from cache-key to the set of functions to update the metadata subscribers.
   */
  getActiveMetadataSubscribers(): Map<string, Set<(entry: CacheEntryMetadata) => void>>;

  getInMemoryCacheStats(prefix?: string): CacheStats;
  getPersistentCacheStats(prefix?: string): Promise<CacheStats>;
  getInMemoryKeys(prefix?: string): string[];

  /**
   * Releases entries from the in-memory cache without touching the underlying persistence layer.
   * Use this to bound memory usage - entries dropped here can be re-hydrated on demand from the
   * persistence layer via `getEntry`.
   *
   * Subscribers are not notified: the value still exists, it's just no longer cached in memory.
   *
   * @param keepFilter - Optional predicate. Return `true` to keep the entry in memory, `false` to
   *   evict it. If omitted, all in-memory entries are evicted.
   * @returns The number of entries evicted from memory.
   */
  freeInMemoryEntries(keepFilter?: (entry: TypedCacheEntry<ValueType>) => boolean): number;

  /**
   * Keeps this cache current with caches in other windows over the same storage, and tells them
   * about this cache's writes, until the returned function is called. Does nothing unless the
   * persistence layer has a crossWindowChannelName and BroadcastChannel exists (not in React Native).
   * @returns A function that stops syncing.
   */
  syncAcrossWindows(): () => void;
}

/**
 * A cache implementation that wraps a persistence layer with memory caching and subscription support
 */
class PersistentCache extends PersistenceLayerBase implements PersistentCacheInterface {
  private memoryCacheThrough = new Map<string, CacheEntry>();
  private subscribers = new Map<string, Set<(entry: CacheEntry) => void>>();
  private metadataSubscribers = new Map<string, Set<(entry: CacheEntryMetadata) => void>>();
  private allSubscribers = new Set<(key: string, entry: CacheEntry) => void>();
  private allMetadataSubscribers = new Set<(key: string, metadata: CacheEntryMetadata) => void>();
  private crossWindowChannel: BroadcastChannel | undefined;
  // Per key: how many local writes are still on their way to the persistence layer
  private localWritesInFlight = new Map<string, number>();
  // Reloads of other windows' changes that are still reading the persistence layer
  private reloadsInFlight = new Set<{ key: string; overtaken: boolean }>();

  constructor(private persistenceLayer: PersistenceLayerInterface) {
    super({ defaultMaxAgeMs: persistenceLayer.defaultMaxAgeMs });
  }

  // A BroadcastChannel never receives its own messages, so a window never reloads its own writes.
  syncAcrossWindows() {
    const { crossWindowChannelName } = this.persistenceLayer;
    if (!crossWindowChannelName || typeof BroadcastChannel === 'undefined') return () => {};
    const channel = new BroadcastChannel(crossWindowChannelName);
    channel.onmessage = ({ data: key }: MessageEvent<string>) => {
      this.reloadEntryChangedElsewhere(key).catch(error => console.error('PersistentCache: reloading a change from another window failed', key, error));
    };
    this.crossWindowChannel = channel;
    return () => {
      channel.close();
      if (this.crossWindowChannel === channel) this.crossWindowChannel = undefined;
    };
  }

  /**
   * Another window wrote `key` to the shared persistence layer. The stale in-memory copy is
   * dropped right away (so reads in the meantime go to the persistence layer), then the new
   * entry is put back in memory - otherwise the next local getEntry would be a memory miss and
   * notify subscribers a second time with a fresh copy of the value (KAIDEV-3162).
   */
  private async reloadEntryChangedElsewhere(key: string) {
    // a local write still on its way to the persistence layer is newer, and notifies subscribers itself
    if (this.localWritesInFlight.has(key)) return;

    const keepInMemory = this.memoryCacheThrough.delete(key) || this.subscribers.has(key);
    const reload = { key, overtaken: false };
    this.reloadsInFlight.add(reload);
    const entry = await this.persistenceLayer.getEntry(key).finally(() => this.reloadsInFlight.delete(reload)) as CacheEntry | undefined;

    // a local write or purge started while we were reading - it is newer than what we read
    if (reload.overtaken) return;

    if (entry && keepInMemory) this.memoryCacheThrough.set(key, entry);
    this.notifySubscribers(key, entry ?? { key, value: undefined, createdAt: 0, expiresAt: 0 });
  }

  private overtakeReloads(isOvertaken: (key: string) => boolean) {
    for (const reload of this.reloadsInFlight) if (isOvertaken(reload.key)) reload.overtaken = true;
  }

  private async writeToPersistenceLayer<T>(key: string, write: () => Promise<T>) {
    this.overtakeReloads(reloadKey => reloadKey === key);
    this.localWritesInFlight.set(key, (this.localWritesInFlight.get(key) ?? 0) + 1);
    try {
      return await write();
    } finally {
      const stillInFlight = this.localWritesInFlight.get(key)! - 1;
      if (stillInFlight) this.localWritesInFlight.set(key, stillInFlight);
      else this.localWritesInFlight.delete(key);
    }
  }

  private tellOtherWindows(key: string) {
    this.crossWindowChannel?.postMessage(key);
  }

  // *******************************************************
  // PersistenceLayer's Async methods
  // *******************************************************

  async set<T extends JsonValue>(key: string, value: T, expiresAt?: number, createdAt?: number, now?: number) {
    const expiresAtMs = this.getExpiresAtMs(expiresAt, now);
    const createdAtMs = this.getCreatedAtMs(createdAt, now);
    const entry: CacheEntry = { key, value, createdAt: createdAtMs, expiresAt: expiresAtMs };
    this.addToMemoryCache(entry);
    const cachedEntry = await this.writeToPersistenceLayer(key, () => this.persistenceLayer.set(key, value, expiresAtMs, createdAtMs, now));
    this.notifySubscribers(key, cachedEntry);
    this.tellOtherWindows(key);
    return cachedEntry;
  }

  async setPrefetched(key: string, value: unknown, expiresAt: number, createdAt?: number, now?: number) {
    const expiresAtMs = this.getExpiresAtMs(expiresAt, now);
    const createdAtMs = this.getCreatedAtMs(createdAt, now);
    const entry: CacheEntry = { key, value, createdAt: createdAtMs, expiresAt: expiresAtMs };
    this.addToMemoryCache(entry, true);
    const cachedEntry = await this.writeToPersistenceLayer(key, () => this.persistenceLayer.set(key, value, expiresAtMs, createdAtMs, now));
    this.notifySubscribers(key, cachedEntry);
    this.tellOtherWindows(key);
    return cachedEntry;
  }

  async purge(key: string) {
    this.memoryCacheThrough.delete(key);
    this.notifySubscribers(key, { key, value: undefined, createdAt: 0, expiresAt: 0 });
    const purged = await this.writeToPersistenceLayer(key, () => this.persistenceLayer.purge(key));
    this.tellOtherWindows(key);
    return purged;
  }

  async getEntry(key: string, now?: number, prefetchOnly?: boolean) {
    const entry = this.memoryCacheThrough.get(key);
    if (entry && !isCacheEntryExpired(entry, now)) {
      return entry;
    }
    const persistedEntry = await this.persistenceLayer.getEntry(key, now);
    const cachedEntry = await this.addToMemoryCache(persistedEntry, prefetchOnly);
    if (cachedEntry && !prefetchOnly) this.notifySubscribers(key, cachedEntry);
    return cachedEntry;
  }

  async getMetadata(key: string, now?: number) {
    let entry = this.memoryCacheThrough.get(key);
    if (entry && isCacheEntryExpired(entry, now)) return undefined;
    if (!entry) {
      entry = await this.persistenceLayer.getMetadata(key, now);
    }
    return entry ? { ...entry, value: undefined } : undefined;
  }

  has(key: string, now?: number) {
    return this.persistenceLayer.has(key, now);
  }

  getAllKeys(prefix?: string, now?: number) {
    return this.persistenceLayer.getAllKeys(prefix, now);
  }

  getAllEntries(prefix?: string, now?: number) {
    return this.persistenceLayer.getAllEntries(prefix, now);
  }

  getAllMetadata(prefix?: string, now?: number) {
    return this.persistenceLayer.getAllMetadata(prefix, now);
  }

  async freshen(key: string, expiresAt?: number, now?: number) {
    const expiresAtMs = this.getExpiresAtMs(expiresAt, now);
    const persistentCopyFreshened = await this.persistenceLayer.freshen(key, expiresAtMs, now);
    const entry = this.memoryCacheThrough.get(key);
    if (entry) {
      const newEntry: CacheEntry = { ...entry, expiresAt: expiresAtMs };
      this.memoryCacheThrough.set(key, newEntry);
      this.notifySubscribers(key, newEntry);
    }
    return persistentCopyFreshened || !!entry;
  }

  async freshenAll(expiresAt?: number, prefix?: string, now?: number) {
    let memoryCacheFreshened = 0;
    for (const entry of this.memoryCacheThrough.values()) {
      if (!prefix || entry.key.startsWith(prefix)) {
        const newEntry: CacheEntry = { ...entry, expiresAt: this.getExpiresAtMs(expiresAt, now) };
        memoryCacheFreshened++;
        this.memoryCacheThrough.set(entry.key, newEntry);
        this.notifySubscribers(entry.key, newEntry);
      }
    }
    const persistentCopyFreshened = await this.persistenceLayer.freshenAll(expiresAt, prefix, now);

    // Generally, the set of entries in the memoryCache set is a subset of the entries in the persistent cache, but there may be edge cases where that's not true.
    return Math.max(persistentCopyFreshened, memoryCacheFreshened);
  }

  // Bulk purges aren't announced to other windows. Each window treats expired entries as expired
  // on its own, and an app that purges everything for a new identity reloads its other windows.
  async purgeAllEntries(prefix?: string, now?: number) {
    now = now ?? Date.now();
    const isPurged = (key: string) => key.startsWith(prefix ?? '');
    this.overtakeReloads(isPurged);
    const notifiedKeys = new Set<string>();
    for (const entry of this.memoryCacheThrough.values()) {
      if (isPurged(entry.key)) {
        this.memoryCacheThrough.delete(entry.key);
        notifiedKeys.add(entry.key);
        this.notifySubscribers(entry.key, { key: entry.key, value: undefined, createdAt: entry.createdAt, expiresAt: now });
      }
    }

    // efficiently notify subscribers for keys that were not in the memory cache
    for (const subscriberKey of this.subscribers.keys()) {
      if (notifiedKeys.has(subscriberKey)) continue;
      if (isPurged(subscriberKey)) {
        notifiedKeys.add(subscriberKey);
        this.notifySubscribers(subscriberKey, { key: subscriberKey, value: undefined, createdAt: 0, expiresAt: now });
      }
    }

    // efficiently notify metadata subscribers for keys that were not in the memory cache
    for (const subscriberKey of this.metadataSubscribers.keys()) {
      if (notifiedKeys.has(subscriberKey)) continue;
      if (isPurged(subscriberKey)) {
        notifiedKeys.add(subscriberKey);
        this.notifyMetadataSubscribers(subscriberKey, { key: subscriberKey, value: undefined, createdAt: 0, expiresAt: now });
      }
    }
    return this.persistenceLayer.purgeAllEntries(prefix, now);
  }

  async purgeExpiredEntries(now?: number) {
    now = now ?? Date.now();
    for (const entry of this.memoryCacheThrough.values()) {
      if (isCacheEntryExpired(entry, now)) {
        this.memoryCacheThrough.delete(entry.key);
        this.notifySubscribers(entry.key, { key: entry.key, value: undefined, createdAt: entry.createdAt, expiresAt: now });
      }
    }
    return this.persistenceLayer.purgeExpiredEntries(now);
  }

  async getCacheCount() {
    return this.persistenceLayer.getCacheCount();
  }

  async getCacheSize() {
    return this.persistenceLayer.getCacheSize();
  }

  // *******************************************************
  // PersistentCache's extended methods
  // *******************************************************

  hasSync(key: string) {
    return this.memoryCacheThrough.has(key);
  }

  getSync(key: string) {
    return this.memoryCacheThrough.get(key);
  }

  getMetadataSync(key: string) {
    const entry = this.memoryCacheThrough.get(key);
    return entry ? { ...entry, value: undefined } : undefined;
  }

  subscribe(key: string, callback: (entry: CacheEntry) => void) {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    this.subscribers.get(key)!.add(callback);

    return () => {
      const keySubscribers = this.subscribers.get(key);
      if (keySubscribers) {
        keySubscribers.delete(callback);
        if (keySubscribers.size === 0) {
          this.subscribers.delete(key);
          this.purgeMemoryCacheIfNoSubscribers(key);
        }
      }
    };
  }

  subscribeToMetadata(key: string, callback: (entry: CacheEntryMetadata) => void) {
    if (!this.metadataSubscribers.has(key)) {
      this.metadataSubscribers.set(key, new Set());
    }
    this.metadataSubscribers.get(key)!.add(callback);

    return () => {
      const keySubscribers = this.metadataSubscribers.get(key);
      if (keySubscribers) {
        keySubscribers.delete(callback);
        if (keySubscribers.size === 0) {
          this.metadataSubscribers.delete(key);
        }
      }
    };
  }

  subscribeToAll(callback: (key: string, entry: CacheEntry) => void) {
    this.allSubscribers.add(callback);
    return () => this.allSubscribers.delete(callback);
  }

  subscribeToAllMetadata(callback: (key: string, metadata: CacheEntryMetadata) => void) {
    this.allMetadataSubscribers.add(callback);
    return () => this.allMetadataSubscribers.delete(callback);
  }

  getActiveSubscribers() {
    return this.subscribers;
  }

  getActiveMetadataSubscribers() {
    return this.metadataSubscribers;
  }

  getInMemoryCacheStats(prefix?: string) {
    const result: CacheStats = { count: 0, size: 0 };
    for (const entry of this.memoryCacheThrough.values()) {
      if (prefix && !entry.key.startsWith(prefix)) continue;
      result.count++;
      result.size += JSON.stringify(entry.value).length;
    }
    return result;
  }

  getInMemoryKeys(prefix?: string) {
    const result: string[] = [];
    for (const entry of this.memoryCacheThrough.values()) {
      if (prefix && !entry.key.startsWith(prefix)) continue;
      result.push(entry.key);
    }
    return result;
  }

  async getPersistentCacheStats(prefix?: string) {
    const size = await this.persistenceLayer.getCacheSize();
    const count = await this.persistenceLayer.getCacheCount();
    return { count, size };
  }

  freeInMemoryEntries(keepFilter?: (entry: CacheEntry) => boolean) {
    if (!keepFilter) {
      const freed = this.memoryCacheThrough.size;
      this.memoryCacheThrough.clear();
      return freed;
    }
    let freed = 0;
    for (const entry of this.memoryCacheThrough.values()) {
      if (keepFilter(entry)) continue;
      this.memoryCacheThrough.delete(entry.key);
      freed++;
    }
    return freed;
  }

  // *************************************************************************************
  // Private
  // *************************************************************************************
  private notifySubscribers(key: string, entry: CacheEntry) {
    const keySubscribers = this.subscribers.get(key);
    if (keySubscribers) {
      for (const callback of keySubscribers) {
        callback(entry);
      }
    }
    for (const callback of this.allSubscribers) callback(key, entry);
    this.notifyMetadataSubscribers(key, entry);
  }

  private notifyMetadataSubscribers(key: string, entry: CacheEntry) {
    const metadata: CacheEntryMetadata = { ...entry, value: undefined };
    const keySubscribers = this.metadataSubscribers.get(key);
    if (keySubscribers) {
      for (const callback of keySubscribers) callback(metadata);
    }
    for (const callback of this.allMetadataSubscribers) callback(key, metadata);
  }

  private async addToMemoryCache(entry: CacheEntry | undefined, prefetchOnly?: boolean) {
    if (!entry) return undefined;
    if (prefetchOnly && !this.memoryCacheThrough.has(entry?.key)) return entry;
    this.memoryCacheThrough.set(entry.key, entry);
    return entry;
  }

  private purgeMemoryCacheIfNoSubscribers(key: string) {
    setTimeout(() => {
      if (!this.subscribers.has(key)) {
        this.memoryCacheThrough.delete(key);
      }
    }, NO_SUBSCRIBERS_MEMORY_CACHE_PURGE_DELAY_MS);
  }

  private getExpiresAtMs(expiresAt?: number, now?: number) {
    return toMilliseconds(expiresAt ?? (now || Date.now()) + this.persistenceLayer.defaultMaxAgeMs);
  }

  private getCreatedAtMs(createdAt?: number, now?: number) {
    return toMilliseconds(createdAt || now);
  }
}

/**
 * Creates a cache instance from a persistence layer
 */
export const createPersistentCache = (persistenceLayer: PersistenceLayerInterface = new InMemoryPersistenceLayer()): PersistentCacheInterface =>
  new PersistentCache(persistenceLayer);
